/**
 * Audiologist Performance Dashboard — third tab under Reports & Analytics.
 *
 * Admin (`super_admin`) + Clinic-Owner only. Renders one row per audiologist
 * for the selected window with diagnostic revenue, HA revenue, units fitted,
 * trials given, trial→sale conversion rate, average sale price, and an
 * expandable brand/model breakdown per audiologist.
 *
 * Filters:
 *   • Time period preset — This Month / Last Month / This Quarter / Last
 *                          Quarter / This Year / Last Year / Custom.
 *   • Branch — inherited from the parent OwnerAnalyticsPage header.
 *   • Audiologist — optional single-user narrow.
 *
 * All filters trigger a single fetch that updates every metric together.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { RefreshCw, ChevronDown, ChevronRight, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;

// --- Time-period preset helper ------------------------------------------
// Returns [start, end] as YYYY-MM-DD strings for a given preset key.
// Presets follow calendar semantics (This Month = 1st of current month → today
// at 23:59:59, This Quarter = 1st day of Q's first month → today, etc.).
function rangeFor(preset) {
  const today = new Date();
  const y = today.getFullYear(); const m = today.getMonth();
  const iso = (d) => d.toISOString().slice(0, 10);
  const q = Math.floor(m / 3);
  switch (preset) {
    case 'this_month': {
      const s = new Date(y, m, 1); return [iso(s), iso(today)];
    }
    case 'last_month': {
      const s = new Date(y, m - 1, 1);
      const e = new Date(y, m, 0);   // last day prev month
      return [iso(s), iso(e)];
    }
    case 'this_quarter': {
      const s = new Date(y, q * 3, 1); return [iso(s), iso(today)];
    }
    case 'last_quarter': {
      const s = new Date(y, (q - 1) * 3, 1);
      const e = new Date(y, q * 3, 0);
      return [iso(s), iso(e)];
    }
    case 'this_year':  return [`${y}-01-01`, iso(today)];
    case 'last_year':  return [`${y - 1}-01-01`, `${y - 1}-12-31`];
    default: return [null, null];
  }
}

const PRESETS = [
  { key: 'this_month',   label: 'This Month' },
  { key: 'last_month',   label: 'Last Month' },
  { key: 'this_quarter', label: 'This Quarter' },
  { key: 'last_quarter', label: 'Last Quarter' },
  { key: 'this_year',    label: 'This Year' },
  { key: 'last_year',    label: 'Last Year' },
  { key: 'custom',       label: 'Custom' },
];


export default function AudiologistPerformanceTab({ branchId = '' }) {
  const [preset, setPreset] = useState('this_month');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [userId, setUserId] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [audiologists, setAudiologists] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [expandedUser, setExpandedUser] = useState(new Set());

  // Compute effective window whenever preset changes (except when Custom).
  useEffect(() => {
    if (preset === 'custom') return;
    const [s, e] = rangeFor(preset);
    if (s) setStart(s);
    if (e) setEnd(e);
  }, [preset]);

  // Load audiologist + clinic-owner list for the filter dropdown.
  // Uses `/api/users` (authenticated, tenant-scoped, returns active staff);
  // the previous `/staff` call always 404'd, which silently fell back to
  // `/ha/analytics/audiologists` — that endpoint only surfaces users with
  // HA-sale history, so zero-activity audiologists (never fit an aid yet)
  // never appeared in the picker. This mirrors the backend allowlist at
  // ha_analytics.audiologist_performance (aud_roles = audiologist +
  // clinic_owner).
  useEffect(() => { (async () => {
    try {
      const r = await axios.get(`${API}/users`);
      const list = (r.data || []).filter(
        (u) => u.active !== false && (u.role === 'audiologist' || u.role === 'clinic_owner'),
      );
      // Sort alphabetically by name so the picker order is stable and
      // doesn't jump around when sales activity changes.
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setAudiologists(list);
    } catch { /* soft dep — table still renders with dropdown empty */ }
  })(); }, []);

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      if (branchId) params.branch_id = branchId;
      if (userId) params.user_id = userId;
      // "Show all audiologists" toggle — surfaces zero-activity rows so
      // owners can spot underperformers. Backend ignores this flag when
      // user_id is set (single-user drill-down always returns the row).
      if (showAll) params.include_zero_activity = true;
      const r = await axios.get(`${API}/ha/analytics/audiologist-performance`, { params });
      setData(r.data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        setErr('This dashboard is reserved for Admin (super_admin) and Clinic-Owner roles.');
      } else {
        setErr(e?.response?.data?.detail || 'Failed to load audiologist performance.');
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // Every filter change re-fetches.
  useEffect(() => {
    if (!start && preset !== 'custom') return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end, branchId, userId, showAll]);

  const toggleExpand = (uid) => {
    const next = new Set(expandedUser);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setExpandedUser(next);
  };

  const rows = data?.rows || [];
  const totals = data?.totals || {};

  // Fixed test-id map — used by pytest + qa harness. Column keys never rename.
  const kpiCards = useMemo(() => ([
    { key: 'total_revenue',     label: 'Total Revenue',        value: fmtINR(totals.total_revenue),      accent: 'indigo' },
    { key: 'diagnostic_revenue', label: 'Diagnostic Revenue',   value: fmtINR(totals.diagnostic_revenue), accent: 'blue' },
    { key: 'ha_revenue',        label: 'HA Sales Revenue',     value: fmtINR(totals.ha_revenue),         accent: 'emerald' },
    { key: 'ha_units_fitted',   label: 'Hearing Aids Fitted',  value: totals.ha_units_fitted ?? 0,       accent: 'teal' },
    { key: 'trials_given',      label: 'Trials Given',         value: totals.trials_given ?? 0,          accent: 'amber' },
    { key: 'conversion_pct',    label: 'Trial → Sale',         value: fmtPct(totals.conversion_pct),     accent: 'violet' },
  ]), [totals]);

  const exportCsv = () => {
    if (!rows.length) return;
    const header = ['Audiologist', 'Role', 'Diagnostic Revenue', 'HA Revenue', 'Total Revenue', 'HA Units Fitted', 'Trials Given', 'Trials Converted', 'Conversion %', 'Avg Sale Price'];
    const lines = [header.join(',')];
    for (const r of rows) {
      lines.push([
        `"${r.name.replace(/"/g, '""')}"`, r.role,
        r.diagnostic_revenue, r.ha_revenue, r.total_revenue,
        r.ha_units_fitted, r.trials_given, r.trials_converted,
        r.conversion_pct, r.avg_sale_price,
      ].join(','));
    }
    lines.push([
      '"TOTAL"', '',
      totals.diagnostic_revenue, totals.ha_revenue, totals.total_revenue,
      totals.ha_units_fitted, totals.trials_given, totals.trials_converted,
      totals.conversion_pct, totals.avg_sale_price,
    ].join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audiologist_performance_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="audiologist-performance-tab">
      {/* Filter strip */}
      <div className="flex items-center justify-between gap-3 flex-wrap bg-slate-50 border border-slate-200 rounded-lg p-3">
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <label className="inline-flex items-center gap-1 text-slate-600">
            <span className="font-semibold uppercase tracking-wider text-[10px] text-slate-500">Period</span>
            <select
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
              data-testid="aud-perf-preset"
              className="border border-slate-300 rounded px-2 py-1 text-xs font-semibold bg-white"
            >
              {PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </label>
          {preset === 'custom' && (
            <>
              <label className="inline-flex items-center gap-1 text-slate-600">
                From <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="aud-perf-start" className="border border-slate-300 rounded px-1 py-0.5 text-xs" />
              </label>
              <label className="inline-flex items-center gap-1 text-slate-600">
                To <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="aud-perf-end" className="border border-slate-300 rounded px-1 py-0.5 text-xs" />
              </label>
            </>
          )}
          <label className="inline-flex items-center gap-1 text-slate-600">
            <span className="font-semibold uppercase tracking-wider text-[10px] text-slate-500">Audiologist</span>
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              data-testid="aud-perf-user-filter"
              className="border border-slate-300 rounded px-2 py-1 text-xs font-semibold bg-white"
            >
              <option value="">All audiologists</option>
              {audiologists.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
          </label>
          {/* "Show all" toggle — surfaces zero-activity audiologists so owners
              can spot underperformers. Disabled (and forced off) when a
              specific audiologist is picked, since drill-down always returns
              that user's row regardless. */}
          <label
            className={`inline-flex items-center gap-1.5 select-none text-[11px] font-semibold rounded px-2 py-1 border ${
              userId
                ? 'text-slate-400 border-slate-200 bg-slate-50 cursor-not-allowed'
                : 'text-slate-700 border-slate-300 bg-white cursor-pointer hover:bg-slate-50'
            }`}
            title={userId
              ? 'Clear the audiologist filter to toggle full-roster view'
              : 'Include audiologists with zero activity in this window'}
          >
            <input
              type="checkbox"
              checked={showAll && !userId}
              disabled={!!userId}
              onChange={(e) => setShowAll(e.target.checked)}
              data-testid="aud-perf-show-all"
              className="h-3.5 w-3.5 accent-indigo-600"
            />
            Show all audiologists
          </label>
          <span className="text-[10px] text-slate-500 italic">Branch inherited from header · {branchId ? 'scoped' : 'all branches'}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportCsv}
            disabled={!rows.length}
            data-testid="aud-perf-csv"
            className="text-[11px] font-semibold text-indigo-700 hover:underline inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download size={11} /> CSV
          </button>
          <button
            onClick={load}
            disabled={loading}
            data-testid="aud-perf-refresh"
            className="text-[11px] text-indigo-600 font-semibold hover:underline inline-flex items-center gap-1"
          >
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {err && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-md p-3 text-xs" data-testid="aud-perf-err">{err}</div>
      )}

      {/* Summary KPI strip — updates alongside the table */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="aud-perf-kpis">
        {kpiCards.map(k => (
          <div
            key={k.key}
            data-testid={`aud-perf-kpi-${k.key}`}
            className={`border rounded-md px-3 py-2 bg-${k.accent}-50 border-${k.accent}-200 text-${k.accent}-800`}
          >
            <div className="text-[9px] font-semibold uppercase tracking-wider">{k.label}</div>
            <div className="text-xl font-bold tabular-nums">{k.value ?? '—'}</div>
          </div>
        ))}
      </div>

      {/* Main table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="aud-perf-table-card">
        <table className="w-full text-xs" data-testid="aud-perf-table">
          <thead className="bg-slate-50">
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="py-2 px-3 w-6" />
              <th className="py-2 px-3">Audiologist</th>
              <th className="py-2 px-2 text-right">Diagnostic Rev.</th>
              <th className="py-2 px-2 text-right">HA Sales Rev.</th>
              <th className="py-2 px-2 text-right">Total Rev.</th>
              <th className="py-2 px-2 text-right">HAs Fitted</th>
              <th className="py-2 px-2 text-right">Trials Given</th>
              <th className="py-2 px-2 text-right">Trial → Sale</th>
              <th className="py-2 px-2 text-right">Avg Sale ₹</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={9} className="py-8 text-center italic text-slate-400">No audiologist activity in this window.</td></tr>
            )}
            {rows.map(r => {
              const isOpen = expandedUser.has(r.user_id);
              return (
                <React.Fragment key={r.user_id}>
                  <tr
                    onClick={() => toggleExpand(r.user_id)}
                    className="border-b border-slate-100 hover:bg-indigo-50/40 cursor-pointer"
                    data-testid={`aud-perf-row-${r.user_id}`}
                  >
                    <td className="py-2 px-3 text-slate-400">
                      {isOpen
                        ? <ChevronDown size={13} />
                        : <ChevronRight size={13} />}
                    </td>
                    <td className="py-2 px-3">
                      <div className="font-semibold text-slate-800">{r.name}</div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-wide">{r.role}</div>
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums font-mono">{fmtINR(r.diagnostic_revenue)}</td>
                    <td className="py-2 px-2 text-right tabular-nums font-mono">{fmtINR(r.ha_revenue)}</td>
                    <td className="py-2 px-2 text-right tabular-nums font-mono font-bold text-indigo-700">{fmtINR(r.total_revenue)}</td>
                    <td className="py-2 px-2 text-right tabular-nums font-bold text-emerald-700">{r.ha_units_fitted}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.trials_given}</td>
                    <td className="py-2 px-2 text-right">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${r.conversion_pct >= 50 ? 'bg-emerald-100 text-emerald-800' : r.conversion_pct >= 25 ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'}`}>
                        {fmtPct(r.conversion_pct)}
                      </span>
                      <span className="text-[9px] text-slate-500 ml-1">({r.trials_converted}/{r.trials_given})</span>
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums font-mono">{fmtINR(r.avg_sale_price)}</td>
                  </tr>
                  {isOpen && (
                    <tr data-testid={`aud-perf-detail-${r.user_id}`}>
                      <td />
                      <td colSpan={8} className="py-3 px-3 bg-slate-50/70 border-b border-slate-100">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                          Brand · Model breakdown
                        </div>
                        {r.brand_model.length === 0 ? (
                          <div className="text-[11px] italic text-slate-400">No HA sales in window.</div>
                        ) : (
                          <BrandModelBreakdown rows={r.brand_model} />
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-slate-100 border-t-2 border-slate-300">
              <tr className="font-bold text-xs" data-testid="aud-perf-totals">
                <td />
                <td className="py-2 px-3 uppercase tracking-wider text-[10px] text-slate-600">Total ({totals.audiologist_count})</td>
                <td className="py-2 px-2 text-right tabular-nums font-mono">{fmtINR(totals.diagnostic_revenue)}</td>
                <td className="py-2 px-2 text-right tabular-nums font-mono">{fmtINR(totals.ha_revenue)}</td>
                <td className="py-2 px-2 text-right tabular-nums font-mono text-indigo-700">{fmtINR(totals.total_revenue)}</td>
                <td className="py-2 px-2 text-right tabular-nums text-emerald-700">{totals.ha_units_fitted}</td>
                <td className="py-2 px-2 text-right tabular-nums">{totals.trials_given}</td>
                <td className="py-2 px-2 text-right">{fmtPct(totals.conversion_pct)}</td>
                <td className="py-2 px-2 text-right tabular-nums font-mono">{fmtINR(totals.avg_sale_price)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {data?.window && (
        <div className="text-[10px] text-slate-400 italic">
          Window: <b>{data.window.start.slice(0, 10)}</b> → <b>{data.window.end.slice(0, 10)}</b>
          {branchId ? ' · branch-scoped' : ' · all branches'}
          {userId ? ' · single audiologist' : ''}
        </div>
      )}
    </div>
  );
}


// --------------------------------------------------------------------------
// Brand / model breakdown — horizontal bar list. Each row is (brand · model,
// units, revenue) so the audiologist's mix is legible at a glance.
// --------------------------------------------------------------------------
function BrandModelBreakdown({ rows }) {
  const maxRev = Math.max(...rows.map(r => r.revenue), 1);
  return (
    <ul className="space-y-1.5" data-testid="aud-perf-brand-model">
      {rows.map((r, i) => (
        <li key={`${r.brand}-${r.model}-${i}`} className="flex items-center gap-3">
          <span className="text-[11px] font-semibold text-slate-800 w-56 truncate" title={`${r.brand} · ${r.model}`}>
            <span className="text-indigo-700">{r.brand}</span> · {r.model}
          </span>
          <div className="flex-1 bg-slate-200 rounded-full h-2 relative overflow-hidden">
            <div
              className="h-full rounded-full bg-indigo-500"
              style={{ width: `${Math.max(4, Math.round(100 * r.revenue / maxRev))}%` }}
            />
          </div>
          <span className="text-[11px] tabular-nums text-slate-500 w-14 text-right">{r.units} unit{r.units === 1 ? '' : 's'}</span>
          <span className="text-[11px] font-mono tabular-nums font-bold text-slate-700 w-20 text-right">{fmtINR(r.revenue)}</span>
        </li>
      ))}
    </ul>
  );
}
